/*
 * The visit phase this chart is in (BUILD-PLAN Step 4; UI survey §1):
 *   before      no recording here yet (drawer open, no order zone)
 *   recording   a live recording for THIS visit (band + order zone)
 *   processing  this patient's note is being produced
 *   noteReady   this visit has a finished note
 * Derived from the existing RecordingContext values and the tab's own
 * recording rows; nothing is added to the context, and the chart wrapper's
 * hasExistingNote (today-only) is not reused.
 *
 * computeVisitPhase() is pure (unit tested); useVisitPhase() adds the
 * "Show orders without recording" escape, which is cleared when the patient
 * or the appointment changes and kept when recording starts.
 */
import { useCallback, useEffect, useState } from "react";
import { useRecording } from "../../../../../context/RecordingContext";
import { isNoteInFlight, normalizeApptId, sameAppointment } from "../../../../../utils/recordingCompletion";

export const computeVisitPhase = ({
  isGlobalRecording = false,
  recordingVisit = null,
  isProcessing = false,
  lastNotePatientId = null,
  lastCompletedRecording = null,
  patientId = null,
  appointmentId = null,
  visitRows = { state: "none", pendingStatus: null },
}) => {
  const appt = normalizeApptId(appointmentId);
  const samePatient = (p) => p != null && patientId != null && String(p) === String(patientId);
  // A recording on another chart (another patient or another visit) is not
  // a recording here.
  const recHere =
    Boolean(isGlobalRecording) && Boolean(recordingVisit) &&
    (sameAppointment(recordingVisit.appointmentid, appt) || (!appt && samePatient(recordingVisit.patientid)));
  const procHere = Boolean(isProcessing) && samePatient(lastNotePatientId);
  const doneNow =
    Boolean(lastCompletedRecording) &&
    (appt ? sameAppointment(lastCompletedRecording.appointmentId, appt) : samePatient(lastCompletedRecording.patientId));
  const rowsState = (visitRows && visitRows.state) || "none";
  const noteHere = doneNow || rowsState === "ready";
  // A reload in the middle of processing: the visit's newest row is in flight.
  const inFlight = rowsState === "pending" && isNoteInFlight(visitRows.pendingStatus);
  if (recHere) return "recording";
  if (procHere || (inFlight && !noteHere)) return "processing";
  if (noteHere) return "noteReady";
  return "before";
};

export function useVisitPhase({ patientId, appointmentId, visitRows }) {
  const {
    isGlobalRecording, isGlobalPaused, globalElapsedTime, recordingVisit,
    isProcessing, lastNotePatientId, lastCompletedRecording,
  } = useRecording();
  const phase = computeVisitPhase({
    isGlobalRecording, recordingVisit, isProcessing, lastNotePatientId, lastCompletedRecording,
    patientId, appointmentId, visitRows,
  });
  const [escape, setEscape] = useState(false);
  useEffect(() => { setEscape(false); }, [patientId, appointmentId]);
  const openEscape = useCallback(() => setEscape(true), []);
  const closeEscape = useCallback(() => setEscape(false), []);
  return {
    phase,
    before: phase === "before",
    escape,
    orderZoneOpen: phase !== "before" || escape,
    paused: phase === "recording" && Boolean(isGlobalPaused),
    elapsed: phase === "recording" ? Number(globalElapsedTime) || 0 : 0,
    openEscape,
    closeEscape,
  };
}

// "04:12"
export const mmss = (secs) => {
  const s = Math.max(0, Math.floor(Number(secs) || 0));
  return `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
};
